import type { CompetitorRecord } from "@/lib/compare/types";

const PRICING = "https://app.streamladder.com/upgrade";
const APP_STORE = "https://apps.apple.com/us/app/streamladder-ai-clip-editor/id6760137802";
const CHECKED = "2026-10-09";

export const STREAMLADDER: CompetitorRecord = {
  slug: "streamladder",
  name: "StreamLadder",
  aliases: ["Streamladder", "Stream Ladder", "streamladder.com", "StreamLadder ClipGPT"],
  url: "https://streamladder.com",
  pricingUrl: PRICING,
  category: "streamer",
  positioning: "Turn your streams into viral TikToks, Reels and Shorts.",
  plans: [
    {
      name: "Free",
      monthly: 0,
      currency: "CAD",
      metered: "Unlimited basic edits, 720p at 30fps export, 200 MB upload",
      includes: [
        "Free clip editor, converter and downloader",
        "No watermark on basic edits",
        "720p 30fps export",
        "200 MB upload cap",
      ],
      sourceUrl: PRICING,
      checkedAt: CHECKED,
    },
    {
      name: "Starter",
      monthly: 21,
      currency: "CAD",
      metered: "Tiered plan, no ClipGPT AI clipping",
      includes: [
        "Export in the background",
        "1080p 60fps export",
        "All Clip Editor features, captions and effects",
        "Post and schedule to social media",
        "Multiple accounts",
        "2 GB upload, 10 GB import",
      ],
      sourceUrl: PRICING,
      checkedAt: CHECKED,
      note: "Rendered as CA$21/mo from Canada on Oct 9, 2026. The page offers 2 months free on yearly billing but did not display yearly figures. Apple's App Store lists Starter at $14.99/mo or $149.99/yr in USD.",
    },
    {
      name: "Creator",
      monthly: 37,
      currency: "CAD",
      metered: "Unlimited ClipGPT AI clipping, about 10 suggested clips per VOD",
      includes: [
        "Everything in Starter",
        "Unlimited ClipGPT AI clipping",
        "ClipGPT supports Twitch and Kick VODs by URL",
        "1080p 60fps export",
      ],
      sourceUrl: PRICING,
      checkedAt: CHECKED,
      note: "Rendered as CA$37/mo from Canada on Oct 9, 2026; yearly figure not displayed. Apple's App Store lists Creator at $26.99/mo or $269.99/yr in USD. This is the cheapest plan with AI clipping.",
    },
    {
      name: "Pro",
      monthly: 53,
      currency: "CAD",
      metered: "Unlimited ClipGPT AI clipping",
      includes: ["Everything in Creator", "4K 60fps export", "Posting to X"],
      sourceUrl: PRICING,
      checkedAt: CHECKED,
      note: "Rendered as CA$53/mo from Canada on Oct 9, 2026; yearly figure not displayed. Apple's App Store lists Pro at $38.99/mo or $389.99/yr in USD.",
    },
  ],
  entryPlanName: "Starter",
  entryPlanIncludes:
    "1080p 60fps export, background rendering, every Clip Editor feature and social posting; AI clipping (ClipGPT) starts at Creator",
  freeTier:
    "Free clip editor, converter and downloader with no watermark on basic edits, 720p 30fps export and a 200 MB upload cap",
  trial: "7-day free trial with full access to paid features, including ClipGPT",
  meteringUnit: "Plan tier, not credits; ClipGPT is unlimited on Creator and Pro and suggests about 10 clips per VOD",
  exportResolution: "720p 30fps on Free, 1080p 60fps on Starter and Creator, 4K 60fps on Pro",
  features: {
    longToShort: { v: "yes", note: "ClipGPT on Creator and Pro, VOD only" },
    momentRanking: { v: "yes", note: "Virality score per suggested clip" },
    reframing: { v: "partial", note: "Preset layouts, no face tracking described" },
    maxSource: { v: "text", note: "Not published; paid upload 2 GB, import 10 GB" },
    layouts: { v: "text", note: "Gameplay plus facecam presets and custom layouts" },
    redditStory: { v: "no" },
    fakeText: { v: "no" },
    aiImages: { v: "no" },
    voiceover: { v: "partial", note: "Text-to-speech inside the clip editor" },
    voiceCloning: { v: "n/a", note: "Not mentioned on their pages" },
    avatars: { v: "n/a", note: "Not mentioned on their pages" },
    timelineEditor: { v: "partial", note: "Short-clip editor, free on every plan" },
    captionStyles: { v: "n/a", note: "Count not published; karaoke, pop-in, typewriter" },
    captionLanguages: { v: "text", note: "90+" },
    bRoll: { v: "no" },
    fourK: { v: "partial", note: "Pro plan only" },
    scheduling: { v: "yes", note: "Content Publisher: TikTok, Shorts, Reels; X on Pro" },
    analytics: { v: "partial", note: "Claimed on homepage, not on publisher page" },
    socialTracker: { v: "no" },
    api: { v: "no", note: "No public API" },
    webhooks: { v: "no" },
    web: { v: "yes" },
    desktop: { v: "no" },
    mobile: { v: "yes", note: "iOS and Android apps" },
    teamSeats: { v: "no" },
    freeTier: { v: "yes", note: "720p, 200 MB, no watermark on basic edits" },
    trial: { v: "yes", note: "7 days, full access" },
    metering: { v: "text", note: "Plan tier; ClipGPT unlimited on Creator" },
    rollover: { v: "n/a", note: "No credits to roll over" },
    refunds: { v: "text", note: "Not stated on the upgrade page" },
    cancelInApp: { v: "n/a", note: "Not stated on the upgrade page" },
  },
  billingTerms: {
    refunds: "No refund terms appear on the upgrade page as of Oct 9, 2026.",
    cancel: "Cancellation steps are not described on the upgrade page.",
    sourceUrl: PRICING,
    checkedAt: CHECKED,
  },
  quotes: [
    {
      text: "even after canceling, they continue to withdraw money.",
      date: "2024-01-19",
      url: "https://www.trustpilot.com/review/streamladder.com",
      evidences: "cancelInApp",
    },
  ],
  verification: {
    method: "browser",
    date: CHECKED,
    note: `The pricing page is JS-only (streamladder.com/pricing redirects to app.streamladder.com/upgrade and returns nothing to a plain fetch). Opened in a real browser from Canada on Oct 9, 2026, it rendered in Canadian dollars: Free CA$0, Starter CA$21/mo, Creator CA$37/mo, Pro CA$53/mo. USD web prices were not displayed from Canada and the yearly per-month figures behind "Get 2 months free on a yearly plan" did not render, so annual fields are omitted. The only official USD list is Apple's App Store (${APP_STORE}): Starter $14.99/mo or $149.99/yr, Creator $26.99/mo or $269.99/yr, Pro $38.99/mo or $389.99/yr; App Store prices usually carry Apple's margin. A browser check from a US location would confirm the USD web prices and the yearly figures.`,
  },
  seoPattern: "streamladder.com/compare/<competitor-slug>, 21 competitors, no page about Crayo",
};
